import React, { ReactElement } from 'react';
import { TabItem } from '../TabItem/tabItem';
import { TabPanel } from '../TabPanel/tabPanel';
import { TabProps } from '../Tab/tab';

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  handleTabChange: (index: number) => void;
  isNested?: boolean;
  activeTabIndex: number;
  children: React.ReactNode;
  className?: string;
}

const Tabs = ({
  children,
  handleTabChange,
  activeTabIndex,
  isNested,
  className = '',
  ...props
}: TabsProps) => {
  // Filter out any children that are not valid Tab components
  const tabs = React.Children.toArray(children).filter(
    (child): child is ReactElement<TabProps> =>
      React.isValidElement<TabProps>(child) && !!child.props?.id && !!child.props?.title
  );

  if (tabs.length < React.Children.toArray(children).length) {
    console.error(
      'You can only pass <Tab> components with defined id and title as children of the <Tabs> component.'
    );
  }

  return (
    <div className={`prelight ${className}`} {...props}>
      <div
        role="tablist"
        className={`preflight ds-mb-5 ds-rounded ${isNested && '!ds-mb-4 ds-bg-neutrals-lighter ds-px-2'}`}
      >
        {tabs.map((tab, index) => {
          return (
            <TabItem
              key={`tab-${index}`}
              handleClick={() => handleTabChange(index)}
              isActive={activeTabIndex === index}
              badgeText={tab.props.badgeText}
              isNested={isNested}
              {...tab.props}
            />
          );
        })}
      </div>
      {tabs.map((tab, index) => {
        return (
          <TabPanel
            id={tab.props.id}
            key={`panel-${index}`}
            hidden={activeTabIndex !== index}
          >
            {tab.props.children}
          </TabPanel>
        );
      })}
    </div>
  );
};

export default Tabs;
